import React, { useState, useEffect } from 'react';
import { useSponsorshipApi } from '../../../Hooks/useSponsorshipApi';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Textarea } from '../../../ui/textarea';
import { Badge } from '../../../ui/badge';

/**
 * SponsorForm Modal Component
 * Handles create and edit operations for sponsors with v26.2 fields
 */
const SponsorForm = ({ sponsor, onClose, onSuccess }) => {
  const { sponsors: sponsorsApi, categories: categoriesApi } = useSponsorshipApi();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    industry_category_id: '',
    title: '',
    description: '',
    website_url: '',
    logo_url: '',
    contact_email: '',
    sort_order: 1,
    media: [],
    metadata: {
      is_premium: false,
      hex_color: '#0044FF'
    }
  });

  const [mediaItem, setMediaItem] = useState({
    type: 'image',
    url: '',
    alt_text: '',
    thumbnail_url: '',
    duration_seconds: 0,
    autoplay: false
  });

  useEffect(() => {
    loadCategories();
    if (sponsor) {
      setFormData({
        name: sponsor.name || '',
        industry_category_id: sponsor.industry_category_id || '',
        title: sponsor.title || '',
        description: sponsor.description || '',
        website_url: sponsor.website_url || '',
        logo_url: sponsor.logo_url || '',
        contact_email: sponsor.contact_email || '',
        sort_order: sponsor.sort_order || 1,
        media: sponsor.media || [],
        metadata: sponsor.metadata || { is_premium: false, hex_color: '#0044FF' }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sponsor]);

  const loadCategories = async () => {
    try {
      const response = await categoriesApi.list();
      const categoriesData = response?.payload?.categories || response?.categories || [];
      setCategories(categoriesData);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.startsWith('metadata.')) {
      const metadataField = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        metadata: {
          ...prev.metadata,
          [metadataField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleAddMedia = () => {
    if (!mediaItem.url) {
      setError('Media URL is required');
      return;
    }

    setFormData(prev => ({
      ...prev,
      media: [...prev.media, { ...mediaItem }]
    }));

    setMediaItem({
      type: 'image',
      url: '',
      alt_text: '',
      thumbnail_url: '',
      duration_seconds: 0,
      autoplay: false
    });
    setError(null);
  };

  const handleRemoveMedia = (index) => {
    setFormData(prev => ({
      ...prev,
      media: prev.media.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (sponsor) {
        await sponsorsApi.update(sponsor.id || sponsor._id, formData);
      } else {
        await sponsorsApi.create(formData);
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save sponsor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="w-full max-w-4xl my-8 rounded-lg border bg-background shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold">
            {sponsor ? 'Edit Sponsor' : 'Create New Sponsor'}
          </h2>
          <Button
            type="button"
            variant="ghost"
            className="h-8 w-8 p-0 text-xl"
            onClick={onClose}
          >
            ×
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="max-h-[calc(100vh-12rem)] overflow-y-auto">
          <div className="space-y-6 px-6 py-4">
            {error && (
              <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}

            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Basic Information
              </h3>
              
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>
                    Sponsor Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>
                    Industry Category <span className="text-destructive">*</span>
                  </Label>
                  <select
                    className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    value={formData.industry_category_id}
                    onChange={(e) => handleInputChange('industry_category_id', e.target.value)}
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat._id || cat.id} value={cat._id || cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>
                    Display Title <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="e.g., TechNova Solutions"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Sort Order</Label>
                  <Input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => handleInputChange('sort_order', parseInt(e.target.value))}
                    min="1"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={3}
                  placeholder="Brief description of the sponsor"
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Website URL</Label>
                  <Input
                    type="url"
                    value={formData.website_url}
                    onChange={(e) => handleInputChange('website_url', e.target.value)}
                    placeholder="https://example.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Contact Email</Label>
                  <Input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => handleInputChange('contact_email', e.target.value)}
                    placeholder="contact@example.com"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Logo URL</Label>
                <Input
                  type="url"
                  value={formData.logo_url}
                  onChange={(e) => handleInputChange('logo_url', e.target.value)}
                  placeholder="https://cdn.example.com/logo.png"
                />
              </div>
            </div>

            {/* Media Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Media Assets
              </h3>
              
              <div className="space-y-4 rounded-md border bg-card p-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Media Type</Label>
                    <select
                      className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      value={mediaItem.type}
                      onChange={(e) => setMediaItem({ ...mediaItem, type: e.target.value })}
                    >
                      <option value="image">Image</option>
                      <option value="video">Video</option>
                      <option value="audio">Audio</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Media URL</Label>
                    <Input
                      type="url"
                      value={mediaItem.url}
                      onChange={(e) => setMediaItem({ ...mediaItem, url: e.target.value })}
                      placeholder="https://cdn.example.com/media.png"
                    />
                  </div>
                </div>

                {mediaItem.type === 'image' && (
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Alt Text</Label>
                      <Input
                        type="text"
                        value={mediaItem.alt_text}
                        onChange={(e) => setMediaItem({ ...mediaItem, alt_text: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Thumbnail URL</Label>
                      <Input
                        type="url"
                        value={mediaItem.thumbnail_url}
                        onChange={(e) => setMediaItem({ ...mediaItem, thumbnail_url: e.target.value })}
                      />
                    </div>
                  </div>
                )}

                {(mediaItem.type === 'video' || mediaItem.type === 'audio') && (
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="space-y-2">
                      <Label>Duration (seconds)</Label>
                      <Input
                        type="number"
                        value={mediaItem.duration_seconds}
                        onChange={(e) => setMediaItem({ ...mediaItem, duration_seconds: parseInt(e.target.value) })}
                      />
                    </div>
                    {mediaItem.type === 'video' && (
                      <>
                        <div className="space-y-2">
                          <Label>Thumbnail URL</Label>
                          <Input
                            type="url"
                            value={mediaItem.thumbnail_url}
                            onChange={(e) => setMediaItem({ ...mediaItem, thumbnail_url: e.target.value })}
                          />
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            id="media-autoplay"
                            type="checkbox"
                            checked={mediaItem.autoplay}
                            onChange={(e) => setMediaItem({ ...mediaItem, autoplay: e.target.checked })}
                          />
                          <Label htmlFor="media-autoplay">Autoplay</Label>
                        </div>
                      </>
                    )}
                  </div>
                )}

                <Button
                  type="button"
                  variant="secondary"
                  className="mt-2"
                  onClick={handleAddMedia}
                >
                  Add Media
                </Button>
              </div>

              {formData.media.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground">
                    Added Media ({formData.media.length})
                  </h4>
                  {formData.media.map((media, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                    >
                      <div className="flex flex-col gap-1">
                        <Badge variant="info" className="w-fit capitalize">
                          {media.type}
                        </Badge>
                        <span className="truncate text-muted-foreground max-w-xs">
                          {media.url}
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveMedia(index)}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Metadata Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Metadata
              </h3>
              
              <div className="flex flex-wrap items-center gap-6">
                <div className="flex items-center space-x-2">
                  <input
                    id="is-premium"
                    type="checkbox"
                    checked={formData.metadata.is_premium}
                    onChange={(e) => handleInputChange('metadata.is_premium', e.target.checked)}
                  />
                  <Label htmlFor="is-premium">Premium Sponsor</Label>
                </div>

                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 overflow-hidden rounded-md border">
                    <input
                      type="color"
                      className="h-full w-full cursor-pointer border-0 bg-transparent p-0"
                      value={formData.metadata.hex_color}
                      onChange={(e) => handleInputChange('metadata.hex_color', e.target.value)}
                    />
                  </div>
                  <div className="w-32">
                    <Label htmlFor="brand-color">Brand Color (Hex)</Label>
                    <Input
                      id="brand-color"
                      type="text"
                      value={formData.metadata.hex_color}
                      onChange={(e) => handleInputChange('metadata.hex_color', e.target.value)}
                      placeholder="#0044FF"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t bg-muted/40 px-6 py-4 sticky bottom-0">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : (sponsor ? 'Update Sponsor' : 'Create Sponsor')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SponsorForm;
